import { IDeveloperField } from 'dromo-uploader-react'
import {
  BuildingStatus,
  BuildingStatusAPIResponse,
  MarketSegment,
  MarketSegmentAPIResponse,
} from '@black-bear-energy/black-bear-energy-common'
import {
  addressSchema,
  citySchema,
  stateSchema,
  zipCodeSchema,
  latSchema,
  longSchema,
  nameSchema,
  notesSchema,
} from '../DromoUploader/commonFieldSchemas'
import { validateZipcode } from '../DromoUploader/zipCodeValidation'
import { validateDate } from '../DromoUploader/dateValidation'
import {
  validateLength,
  validateRequired,
} from '../DromoUploader/stringValidation'
import { RowValidations } from '../DromoUploader/updateCellMessages'
import { validateNumberMinMax } from '../DromoUploader/numberValidation'

const marketSegmentOptions = (
  Object.keys(MarketSegment) as Array<keyof typeof MarketSegment>
).map((key) => {
  if (key === 'Residential') {
    return {
      label: 'Residential (single-family home)',
      value: MarketSegmentAPIResponse[key],
    }
  }
  return {
    label: MarketSegment[key],
    value: MarketSegmentAPIResponse[key],
  }
})

const buildingStatusOptions = (
  Object.keys(BuildingStatus) as Array<keyof typeof BuildingStatus>
).map((key) => {
  return {
    label: BuildingStatus[key],
    value: BuildingStatusAPIResponse[key],
  }
})

export const qbRecordIdSchema: IDeveloperField = {
  label: 'QB Record Id',
  key: 'qbRecordId',
  type: ['number', { preset: 'integer' }],
}

export const fieldSchema: IDeveloperField[] = [
  {
    label: 'Acquisition Date',
    key: 'acquireDate',
    type: 'date',
  },
  addressSchema,
  {
    label: 'Asset Manager',
    key: 'assetManager',
  },
  {
    label: 'Building Area (sqft)',
    key: 'buildingArea',
    type: ['number', { preset: 'integer' }],
    alternateMatches: ['sq footage'],
  },
  {
    label: 'Building Status',
    key: 'buildingStatus',
    type: 'select',
    selectOptions: buildingStatusOptions,
  },
  {
    label: 'Building Subtype',
    key: 'buildingSubType',
  },
  {
    label: 'Built Year',
    key: 'buildingBuildYear',
    type: [
      'number',
      {
        displayFormat: { thousandSeparated: false },
        round: 0,
      },
    ],
  },
  citySchema,
  {
    label: 'Client Designation',
    key: 'clientDesignation',
  },
  {
    label: 'County',
    key: 'county',
  },
  {
    label: 'Has Existing PV/Solar?',
    key: 'hasSolar',
    type: 'checkbox',
  },
  {
    label: 'Has Tenants?',
    key: 'hasTenants',
    type: 'checkbox',
  },
  {
    label: 'Investment Fund',
    key: 'clientInvestmentFund',
  },
  {
    label: 'JV Partner',
    key: 'jvPartnerName',
  },
  latSchema,
  {
    label: 'Lender Name',
    key: 'lenderName',
  },
  longSchema,
  {
    label: 'Market Segment - Sector',
    key: 'marketSegment',
    type: 'select',
    selectOptions: marketSegmentOptions,
    alternateMatches: ['asset type'],
  },
  notesSchema,
  {
    label: 'Number of Units',
    key: 'unitCount',
    type: ['number', { preset: 'integer' }],
  },
  {
    label: 'Owners Association',
    key: 'ownersAssociation',
    type: 'select',
    selectOptions: [
      { label: 'Yes', value: 'Yes', alternateMatches: ['true', '1'] },
      { label: 'No', value: 'No', alternateMatches: ['false', '0'] },
    ],
  },
  {
    label: 'Portfolio Company',
    key: 'portfolioCompany',
  },
  {
    label: 'Property Manager',
    key: 'propertyManager',
  },
  {
    label: 'Roof Install Year',
    key: 'roofInstallYear',
    type: [
      'number',
      {
        displayFormat: { thousandSeparated: false },
        round: 0,
      },
    ],
  },
  {
    label: 'Roof Type',
    key: 'roofType',
  },
  nameSchema,
  stateSchema,
  {
    label: 'Tenant Name(s)',
    key: 'tenantName',
  },
  zipCodeSchema,
  // hidden columns
  {
    label: 'Existing Record Id',
    key: 'existingRecordId',
    hidden: true,
  },
  {
    label: 'Row UUID',
    key: 'rowId',
    hidden: true,
  },
  {
    label: 'Is Potentially Sold',
    key: 'maybeSold',
    hidden: true,
  },
  {
    label: 'Is Geocode Valid',
    key: 'isGeocodeValid',
    hidden: true,
  },
]

export const validations: RowValidations = {
  acquireDate: (value: string) => validateDate(value),
  address: (value: string) =>
    validateLength(value, 150).concat(validateRequired(value)),
  assetManager: (value: string) => validateLength(value, 100),
  buildingArea: (value: string) => validateNumberMinMax(value, 500, 10000000),
  buildingBuildYear: (value: string) => validateDate(value),
  city: (value: string) =>
    validateLength(value, 150).concat(validateRequired(value)),
  clientDesignation: (value: string) => validateLength(value, 100),
  clientInvestmentFund: (value: string) => validateLength(value, 100),
  county: (value: string) => validateLength(value, 50),
  jvPartnerName: (value: string) => validateLength(value, 150),
  latitude: (value: string) =>
    // valid range for USA and Canada
    validateNumberMinMax(value, 12, 90).concat(validateRequired(value)),
  longitude: (value: string) =>
    // valid range for USA and Canada
    validateNumberMinMax(value, -170, -51).concat(validateRequired(value)),
  name: (value: string) =>
    validateLength(value, 150).concat(validateRequired(value)),
  propertyManager: (value: string) => validateLength(value, 150),
  roofInstallYear: (value: string) => validateDate(value),
  siteAction: (value: string) => validateRequired(value),
  state: (value: string) => validateRequired(value),
  unitCount: (value: string) => validateNumberMinMax(value, 0, 2000),
  zipCode: (value: string) =>
    validateZipcode(value).concat(validateRequired(value)),
}
